import type {
  FacilityOption,
  VenueOption,
} from '../../services/game/gameOptionsService'

/**
 * Location handling for the event form's Location / Facility pickers.
 *
 * Pure and platform-agnostic so web and mobile share one set of rules for what
 * a venue selection means.
 */

/**
 * Label for the custom-location action.
 *
 * Rendered as a FOOTER row under the option list — the same treatment (and the
 * same `colors.PRIMARY`) the Game modal gives "Create a new location", so the
 * two read as the same kind of escape hatch rather than as another venue.
 */
export const CUSTOM_LOCATION_LABEL = 'Use A Custom Location'

/**
 * Value shown in the Location select once the custom choice is made.
 *
 * Not one of the dropdown's options — it is appended only while the choice is
 * active, so the field reads as answered instead of empty. `venueId` stays ''
 * and this never reaches the API.
 */
export const CUSTOM_LOCATION_VALUE = '__custom_location__'

/**
 * The zone an event at this venue is authored in.
 *
 * Checks the venue's own `timezone` before the address's: both are returned and
 * either may be null. Returns null when the venue carries neither, so the
 * caller keeps the form's existing zone rather than blanking it.
 */
export const resolveVenueTimezone = (
  venue?: VenueOption | null
): string | null => {
  const candidates = [venue?.timezone, venue?.address?.timezone]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim()
    }
  }

  return null
}

/** A venue's display name, from either key the API populates. */
export const venueLabel = (venue: VenueOption): string =>
  venue.name_full?.trim() || venue.name?.trim() || ''

/** A facility's display name. */
export const facilityLabel = (facility: FacilityOption): string =>
  facility.name?.trim() || ''

export interface SelectOption {
  value: string
  label: string
}

/**
 * Location options — real venues only.
 *
 * The custom-location action is NOT one of these: it is a footer row under the
 * list, styled like the Game modal's "Create a new location", so it is not
 * mistaken for a venue named "Use A Custom Location".
 */
export const buildVenueOptions = (venues: VenueOption[]): SelectOption[] =>
  venues
    .map((venue) => ({ value: venue.id, label: venueLabel(venue) }))
    .filter((option) => option.label !== '')

/**
 * Facility options for the selected venue, read from the venue record itself.
 *
 * `/venues` already nests them, so this needs no request of its own. Inactive
 * facilities are dropped — they are still returned, but must not be selectable
 * for a NEW event.
 */
export const buildFacilityOptions = (
  venue?: VenueOption | null
): SelectOption[] =>
  (venue?.facilities ?? [])
    .filter((facility) => facility.is_active !== false)
    .map((facility) => ({ value: facility.id, label: facilityLabel(facility) }))
    .filter((option) => option.label !== '')

/**
 * Whether the ADDRESS fields are shown.
 *
 * Only for an EXPLICIT custom-location choice — not merely "no venue", which
 * is also the untouched state and the state after clearing the field. A fresh
 * event should not open with an address block nobody asked for.
 *
 * `hasLocationPicker` is false on every surface that offers no Location select
 * at all (a team-owned event), where the address fields are the only way to say
 * where the event is and so are always shown.
 */
export const showsAddressFields = (
  isCustomLocation: boolean,
  hasLocationPicker: boolean
): boolean => !hasLocationPicker || isCustomLocation

/**
 * Whether the Timezone select is shown.
 *
 * Visible whenever the event's zone is not already settled by a venue:
 * - no venue selected → the admin picks the zone (the pre-venue behaviour)
 * - a venue WITHOUT a zone → still the admin's to pick; the venue answers
 *   "where", not "when"
 * - a venue WITH a zone → hidden, because that zone wins and an editable
 *   control beside it would imply otherwise
 */
export const showsTimezoneField = (
  venueId: string,
  venue?: VenueOption | null
): boolean => venueId.trim() === '' || resolveVenueTimezone(venue) === null
